/* ============================================
   ComponentesBuilder — lista editable de componentes de un paquete
   de servicio gestionado. Cada componente tiene su propio proveedor;
   el paquete los agrupa como unidad de cobro/corte.

   El campo "Recurso" se alimenta del INVENTARIO (`GET /api/hosting/inventario`):
   muestra lo disponible y marca lo que ya está en otro paquete, para no crear
   dos planes que cobren (y corten) el mismo contenedor/cuenta.
   ============================================ */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import type { HostingComponente } from '@/lib/types'

// Catálogo de proveedores disponibles como componente. Al elegir uno se autocompletan
// `tipo` y `kill_mode` (el corte técnico) y cambia el placeholder del recurso_ref.
export const PROVEEDOR_OPCIONES = [
  { value: 'cloudflare_edge', label: 'Cloudflare (borde)', tipo: 'edge', kill_mode: 'kv_gate', placeholder: 'erp.cliente.pe', hint: 'Hostname que se bloquea al suspender (punto de control)' },
  { value: 'hetzner_docker', label: 'Hetzner (Docker)', tipo: 'backend', kill_mode: 'docker_stop', placeholder: 'nombre-del-contenedor', hint: 'Contenedor a detener (docker stop por label)' },
  { value: 'supabase_project', label: 'Supabase', tipo: 'db', kill_mode: 'supabase_pause', placeholder: 'project_ref', hint: 'Pausa manual desde el dashboard de Supabase' },
  { value: 'verpex_whm', label: 'Verpex (WHM)', tipo: 'hosting', kill_mode: 'whm_suspend', placeholder: 'usuario cPanel', hint: 'Suspensión de la cuenta cPanel vía WHM' },
] as const

export interface InventarioItem {
  recurso: string
  descripcion?: string
  en_uso: boolean
  pedido_codigo?: string | null
  fuente: 'whm' | 'erp' | 'docker'
  suspendida?: boolean
  /** Infra del propio ERP (api, postgres, …): se lista para que se vea, pero no es asignable. */
  bloqueado?: boolean
  estado_docker?: string | null
}

interface InventarioResp {
  proveedor: string
  fuente: 'whm' | 'erp' | 'docker'
  aviso: string | null
  items: InventarioItem[]
}

const MANUAL = '__manual__'
const norm = (s?: string | null) => String(s || '').trim().toLowerCase()

export function nuevoComponente(proveedor = 'cloudflare_edge'): HostingComponente {
  const opt = PROVEEDOR_OPCIONES.find(o => o.value === proveedor) || PROVEEDOR_OPCIONES[0]
  return { proveedor: opt.value, tipo: opt.tipo, recurso_ref: '', kill_mode: opt.kill_mode }
}

interface Props {
  value: HostingComponente[]
  onChange: (comps: HostingComponente[]) => void
  /** Avisa al wizard si algún recurso ya pertenece a otro paquete (para bloquear el paso). */
  onConflictoChange?: (hayConflicto: boolean) => void
}

export default function ComponentesBuilder({ value, onChange, onConflictoChange }: Props) {
  const api = useApi()
  const [inventario, setInventario] = useState<Record<string, InventarioResp>>({})
  const [cargando, setCargando] = useState<Record<string, boolean>>({})
  // Índices donde el admin eligió "escribir manualmente" (recurso fuera del inventario).
  const [manual, setManual] = useState<Record<number, boolean>>({})

  const proveedoresUsados = useMemo(
    () => [...new Set(value.map(c => c.proveedor).filter(Boolean) as string[])].sort().join(','),
    [value],
  )

  // Carga perezosa del inventario: sólo los proveedores que el paquete usa, una vez cada uno.
  useEffect(() => {
    const provs = proveedoresUsados ? proveedoresUsados.split(',') : []
    provs.forEach(async prov => {
      if (inventario[prov] || cargando[prov]) return
      setCargando(prev => ({ ...prev, [prov]: true }))
      try {
        const resp = await api.get<InventarioResp>(`/api/hosting/inventario?proveedor=${encodeURIComponent(prov)}`)
        setInventario(prev => ({ ...prev, [prov]: resp }))
      } catch {
        // Sin inventario el campo sigue siendo texto libre: no bloqueamos el alta.
        setInventario(prev => ({
          ...prev,
          [prov]: { proveedor: prov, fuente: 'erp', aviso: 'No se pudo cargar el inventario. Escribí el recurso a mano.', items: [] },
        }))
      } finally {
        setCargando(prev => ({ ...prev, [prov]: false }))
      }
    })
  }, [proveedoresUsados, inventario, cargando, api])

  const itemDe = (comp: HostingComponente): InventarioItem | undefined => {
    const items = inventario[comp.proveedor || '']?.items || []
    return items.find(i => norm(i.recurso) === norm(comp.recurso_ref))
  }
  const ocupadoDe = (comp: HostingComponente): InventarioItem | undefined => {
    const it = itemDe(comp)
    return it?.en_uso && !it?.bloqueado ? it : undefined
  }
  // Infra del ERP: asignarla significaría que un impago apague la API o la base.
  const bloqueadoDe = (comp: HostingComponente): InventarioItem | undefined => {
    const it = itemDe(comp)
    return it?.bloqueado ? it : undefined
  }

  // Además del inventario, un mismo recurso repetido dentro del propio paquete también es error.
  const repetidoEnPaquete = (comp: HostingComponente, i: number) =>
    !!norm(comp.recurso_ref) &&
    value.some((o, j) => j < i && o.proveedor === comp.proveedor && norm(o.recurso_ref) === norm(comp.recurso_ref))

  const hayConflicto = value.some((c, i) => !!ocupadoDe(c) || !!bloqueadoDe(c) || repetidoEnPaquete(c, i))
  // Ref para el callback: si dependiéramos de la prop, un arrow inline del padre re-dispararía
  // el efecto en cada render (setState del padre → render → efecto → loop).
  const avisarRef = useRef(onConflictoChange)
  avisarRef.current = onConflictoChange
  useEffect(() => { avisarRef.current?.(hayConflicto) }, [hayConflicto])

  const setComp = (i: number, patch: Partial<HostingComponente>) => {
    onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)))
  }
  const cambiarProveedor = (i: number, proveedor: string) => {
    const opt = PROVEEDOR_OPCIONES.find(o => o.value === proveedor)
    // Al cambiar de proveedor, re-derivamos tipo + kill_mode y limpiamos el recurso
    // (un usuario cPanel no es un nombre de contenedor).
    setComp(i, { proveedor, tipo: opt?.tipo, kill_mode: opt?.kill_mode, recurso_ref: '' })
    setManual(prev => ({ ...prev, [i]: false }))
  }
  const remove = (i: number) => {
    onChange(value.filter((_, idx) => idx !== i))
    // Reindexar el mapa de "manual" para que no se corra al eliminar del medio.
    setManual(prev => {
      const next: Record<number, boolean> = {}
      Object.entries(prev).forEach(([k, v]) => {
        const idx = Number(k)
        if (idx < i) next[idx] = v
        else if (idx > i) next[idx - 1] = v
      })
      return next
    })
  }
  const add = () => onChange([...value, nuevoComponente('hetzner_docker')])

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {value.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', padding: '8px 0' }}>
          Sin componentes. Agrega al menos el borde de Cloudflare (lo que bloquea el acceso al suspender).
        </div>
      )}
      {value.map((comp, i) => {
        const opt = PROVEEDOR_OPCIONES.find(o => o.value === comp.proveedor)
        const inv = inventario[comp.proveedor || '']
        const items = inv?.items || []
        const libres = items.filter(x => !x.en_uso && !x.bloqueado)
        const ocupados = items.filter(x => x.en_uso && !x.bloqueado)
        const bloqueados = items.filter(x => x.bloqueado)
        const ocupado = ocupadoDe(comp)
        const bloqueado = bloqueadoDe(comp)
        const repetido = repetidoEnPaquete(comp, i)
        const enInventario = items.some(x => norm(x.recurso) === norm(comp.recurso_ref))
        // Desplegable sólo si hay algo que desplegar y el valor actual sale del inventario.
        const usarSelect = items.length > 0 && !manual[i] && (enInventario || !norm(comp.recurso_ref))
        return (
          <div
            key={i}
            style={{
              display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr auto',
              alignItems: 'end', padding: 12,
              border: `1px solid var(${ocupado || bloqueado || repetido ? '--danger' : '--border'})`,
              borderRadius: 10, background: 'var(--veil-03)',
            }}
          >
            <div>
              <label className="form-label">Proveedor</label>
              <select className="form-input" value={comp.proveedor} onChange={e => cambiarProveedor(i, e.target.value)}>
                {PROVEEDOR_OPCIONES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Recurso</label>
              {usarSelect ? (
                <select
                  className="form-input"
                  value={comp.recurso_ref || ''}
                  onChange={e => {
                    if (e.target.value === MANUAL) {
                      setManual(prev => ({ ...prev, [i]: true }))
                      setComp(i, { recurso_ref: '' })
                    } else {
                      setComp(i, { recurso_ref: e.target.value })
                    }
                  }}
                >
                  <option value="">— Elegir {opt?.placeholder} —</option>
                  {libres.length > 0 && (
                    <optgroup label="Disponibles">
                      {libres.map(x => (
                        <option key={x.recurso} value={x.recurso}>
                          {x.recurso}{x.descripcion ? ` — ${x.descripcion}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {ocupados.length > 0 && (
                    <optgroup label="Ya asignados (no disponibles)">
                      {ocupados.map(x => (
                        <option key={x.recurso} value={x.recurso} disabled>
                          {x.recurso} — ocupado por {x.pedido_codigo}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {bloqueados.length > 0 && (
                    <optgroup label="Infraestructura del ERP (no asignable)">
                      {bloqueados.map(x => (
                        <option key={x.recurso} value={x.recurso} disabled>
                          {x.recurso} — infraestructura
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <option value={MANUAL}>Escribir manualmente…</option>
                </select>
              ) : (
                <input
                  className="form-input"
                  value={comp.recurso_ref || ''}
                  onChange={e => setComp(i, { recurso_ref: e.target.value })}
                  placeholder={opt?.placeholder}
                  list={items.length > 0 ? `inv-${comp.proveedor}` : undefined}
                />
              )}
              {items.length > 0 && (
                <datalist id={`inv-${comp.proveedor}`}>
                  {items.map(x => <option key={x.recurso} value={x.recurso} />)}
                </datalist>
              )}
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => remove(i)}
              title="Quitar componente"
              style={{ color: 'var(--danger)', marginBottom: 2 }}
            >
              ✕
            </button>
            <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 2 }}>
              {ocupado && (
                <div style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>
                  ⚠ Este recurso ya pertenece al paquete <strong>{ocupado.pedido_codigo}</strong>. Un recurso no puede estar en dos paquetes.
                </div>
              )}
              {bloqueado && (
                <div style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>
                  ⚠ <strong>{bloqueado.recurso}</strong> es infraestructura del ERP: no puede asignarse a un paquete de cliente.
                </div>
              )}
              {!ocupado && !bloqueado && repetido && (
                <div style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>
                  ⚠ Este recurso ya está listado en este mismo paquete.
                </div>
              )}
              {cargando[comp.proveedor || ''] && <div className="form-hint">Cargando inventario…</div>}
              {!cargando[comp.proveedor || ''] && inv?.aviso && <div className="form-hint">{inv.aviso}</div>}
              {!cargando[comp.proveedor || ''] && inv && !inv.aviso && (
                <div className="form-hint">
                  Inventario {inv.fuente === 'whm' ? 'en vivo (WHM)' : inv.fuente === 'docker' ? 'en vivo (Docker)' : 'del ERP'}: {libres.length} disponible(s) · {ocupados.length} ya asignado(s)
                </div>
              )}
              {opt?.hint && <div className="form-hint">{opt.hint}</div>}
            </div>
          </div>
        )
      })}
      <button type="button" className="btn btn-ghost btn-sm" onClick={add} style={{ justifySelf: 'start' }}>
        + Agregar componente
      </button>
    </div>
  )
}
